import { useRef } from 'react';

/**
 * Coordinates a scan's local edits against its background refetches.
 *
 * A refetch that lands while a local edit hasn't yet reached the server can
 * rebuild the on-screen scan root from data that doesn't include that edit,
 * visually reverting it (KAIDEV-4175). This gate lets a fetch defer itself
 * while any local edit is unsynced, and lets an in-flight fetch detect that a
 * new edit landed mid-request so it can retry instead of committing a result
 * that's already stale.
 *
 * `hasPendingLocalEdit` covers the window between "the user made an edit" and
 * "that edit was handed to the sync queue" (roughly: the save debounce still
 * pending). Whether the queue itself has drained *to the server* is tracked
 * separately, by the sync model - this gate doesn't own that.
 */
export type ScanEditSyncGate = {
  /** Call when a local edit starts, before its save debounce/cache write. */
  recordEdit: () => void;
  getEditEpoch: () => number;
  hasPendingLocalEdit: () => boolean;
  setHasPendingLocalEdit: (pending: boolean) => void;
  isRefetchPending: () => boolean;
  setRefetchPending: (pending: boolean) => void;
  /** Calls `callback` once no local edit is pending: now, or when the pending one reaches the sync queue. */
  whenNoPendingLocalEdit: (callback: () => void) => void;
};

export const createScanEditSyncGate = (): ScanEditSyncGate => {
  let editEpoch = 0;
  let pendingLocalEdit = false;
  let refetchPending = false;
  let noPendingLocalEditCallbacks: (() => void)[] = [];

  return {
    recordEdit: () => {
      editEpoch++;
    },
    getEditEpoch: () => editEpoch,
    hasPendingLocalEdit: () => pendingLocalEdit,
    setHasPendingLocalEdit: pending => {
      pendingLocalEdit = pending;
      if (pending) return;
      const callbacks = noPendingLocalEditCallbacks;
      noPendingLocalEditCallbacks = [];
      for (const callback of callbacks) callback();
    },
    isRefetchPending: () => refetchPending,
    setRefetchPending: pending => {
      refetchPending = pending;
    },
    whenNoPendingLocalEdit: callback => {
      if (pendingLocalEdit) noPendingLocalEditCallbacks.push(callback);
      else callback();
    },
  };
};

/** A fresh gate for the given scanId, recreated whenever scanId changes. */
export const useScanEditSyncGate = (scanId: string | undefined): ScanEditSyncGate => {
  const scanIdRef = useRef<string | undefined>(undefined);
  const gateRef = useRef<ScanEditSyncGate | undefined>(undefined);
  if (!gateRef.current || scanIdRef.current !== scanId) {
    gateRef.current = createScanEditSyncGate();
    scanIdRef.current = scanId;
  }
  return gateRef.current;
};
